import type { ReactNode } from "react";
import { forwardRef, useMemo } from "react";

import classNames from "classnames";

import type { CardProps } from "@jti/core-components";
import { Card } from "@jti/core-components";
import { Button } from "@jti/core-components/components/Button";

import { hasButton } from "./utils/hasButton";

import { CardGenericHeaderSection } from "./CardGenericHeaderSection";

import styles from "./CardGeneric.module.scss";

export type CardGenericProps = CardProps & {
  icon?: ReactNode;
  title?: string;
  secondaryTitle?: string;
  description?: string;
  primaryLabel?: string;
  primaryHref?: string;
  primaryOnClick?: () => void;
  secondaryLabel?: string;
  secondaryHref?: string;
  secondaryOnClick?: () => void;
  reverseOrder?: boolean;
  className?: string;
  children?: ReactNode;
};

export const CardGeneric = forwardRef<HTMLDivElement, CardGenericProps>(
  (
    {
      icon,
      title,
      secondaryTitle,
      description,
      primaryLabel,
      primaryHref,
      primaryOnClick,
      secondaryLabel,
      secondaryHref,
      secondaryOnClick,
      children,
      className,
      reverseOrder = false,
      ...restProps
    },
    ref
  ) => {
    const isHeaderSectionVisible = !!(icon || title || secondaryTitle || description);

    const primaryCta = useMemo(
      () => ({
        label: primaryLabel,
        href: primaryHref,
        onClick: primaryOnClick,
      }),
      [primaryLabel, primaryHref, primaryOnClick]
    );
    const secondaryCta = useMemo(
      () => ({
        label: secondaryLabel,
        href: secondaryHref,
        onClick: secondaryOnClick,
      }),
      [secondaryLabel, secondaryHref, secondaryOnClick]
    );
    const { label: primaryButtonLabel, ...primaryButtonProps } = primaryCta;
    const { label: secondaryButtonLabel, ...secondaryButtonProps } = secondaryCta;

    return (
      <div ref={ref} className={classNames(className, styles.container)}>
        <Card {...restProps}>
          <div className={styles.container}>
            {isHeaderSectionVisible && (
              <CardGenericHeaderSection
                icon={icon}
                title={title}
                secondaryTitle={secondaryTitle}
                description={description}
                reverseOrder={reverseOrder}
              />
            )}

            {children && <div className={styles.body}>{children}</div>}

            {(hasButton(primaryCta) || hasButton(secondaryCta)) && (
              <div className={styles.buttonsWrapper}>
                {hasButton(primaryCta) && (
                  <>
                    <Button {...primaryButtonProps} className={styles.button} variant="primary">
                      {primaryButtonLabel ?? ""}
                    </Button>
                    <Button {...primaryButtonProps} className={styles.buttonMobile} size="sm" variant="primary">
                      {primaryButtonLabel ?? ""}
                    </Button>
                  </>
                )}
                {hasButton(secondaryCta) && (
                  <>
                    <Button {...secondaryButtonProps} className={styles.button} variant="secondary">
                      {secondaryButtonLabel ?? ""}
                    </Button>
                    <Button {...secondaryButtonProps} className={styles.buttonMobile} size="sm" variant="secondary">
                      {secondaryButtonLabel ?? ""}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }
);
